import React, { useEffect, useRef, useState } from 'react'
import { ConfigProvider, Grid, Steps, Tooltip, Typography, theme } from 'antd'

/** Минимальная ширина одного шага (px), ниже которой подписи не помещаются — компактный режим */
const MIN_ITEM_WIDTH = 96

/** Подпись шага: мелкий шрифт, перенос по словам (до 3 строк), по центру — без обрезки */
const TITLE_STYLE: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'normal',
  overflowWrap: 'anywhere',
  fontSize: 13,
  lineHeight: 1.3,
  textAlign: 'center',
}

interface PollStepsHeaderProps {
  /** Уже интерполированные названия шагов */
  titles: string[]
  current: number
}

/**
 * Шапка шагов опроса.
 * Десктоп: иконка сверху, название снизу (до 2 строк, слова не режутся), Tooltip с полным названием.
 * Узкий экран (< md) или слишком много шагов на ширину: точки для всех шагов + «Шаг X из N · Название».
 */
export const PollStepsHeader: React.FC<PollStepsHeaderProps> = ({ titles, current }) => {
  const screens = Grid.useBreakpoint()
  const { token } = theme.useToken()
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const node = containerRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    setWidth(node.clientWidth)
    const observer = new ResizeObserver(entries => {
      setWidth(entries[0]?.contentRect.width || 0)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const compact =
    screens.md === false || (width > 0 && width / Math.max(titles.length, 1) < MIN_ITEM_WIDTH)
  const currentTitle = titles[current] || ''
  /** Ширина подписи = ширина шага (по умолчанию antd ограничивает её 140px и режет текст) */
  const itemWidth = width > 0 ? Math.floor(width / Math.max(titles.length, 1)) - 8 : 120

  return (
    <div ref={containerRef} style={{ marginBottom: 20 }}>
      {compact ? (
        <>
          <div
            role="list"
            aria-label="Шаги опроса"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
          >
            {titles.map((title, index) => {
              const active = index === current
              const done = index < current
              return (
                <Tooltip key={index} title={`${index + 1}. ${title}`}>
                  <span
                    role="listitem"
                    aria-label={`Шаг ${index + 1}: ${title}`}
                    aria-current={active ? 'step' : undefined}
                    style={{
                      flex: '0 0 auto',
                      width: active ? 24 : 10,
                      height: 10,
                      borderRadius: 5,
                      background: active
                        ? token.colorPrimary
                        : done
                        ? token.colorPrimaryBorder
                        : token.colorFillSecondary,
                      transition: `all ${token.motionDurationMid}`,
                    }}
                  />
                </Tooltip>
              )
            })}
          </div>
          <Typography.Text>
            <Typography.Text type="secondary">
              Шаг {current + 1} из {titles.length} ·{' '}
            </Typography.Text>
            <Typography.Text strong>{currentTitle}</Typography.Text>
          </Typography.Text>
        </>
      ) : (
        <ConfigProvider
          theme={{
            components: {
              Steps: {
                descriptionMaxWidth: Math.max(80, Math.min(itemWidth, 180)),
                titleLineHeight: 18,
              },
            },
          }}
        >
          <Steps
            size="small"
            current={current}
            labelPlacement="vertical"
            responsive={false}
            items={titles.map(title => ({
              title: (
                <Tooltip title={title}>
                  <span style={TITLE_STYLE}>{title}</span>
                </Tooltip>
              ),
            }))}
          />
        </ConfigProvider>
      )}
    </div>
  )
}

export default PollStepsHeader
